/**
 * Command Center Component - Main command center page for Ford PM Application
 * 
 * Features:
 * - Critical outage alert banner
 * - Sites at a glance overview with status cards
 * - Real-time monitoring with 40-second polling
 * - Site status indicators (Healthy, Down, etc.)
 * - Uptime percentage display
 * - Uses same fetchAllData pattern as Dashboard
 * 
 * @author CTS - Ford PM UI Team
 */

import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { getSitesApi, getDevicesApi } from '../../api/dashboardApi';

// Material UI imports
import {
  Typography,
  Box,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Warning as WarningIcon,
} from '@mui/icons-material';

// Theme imports
import { FORD_TYPOGRAPHY } from '../../theme/fordTheme.js';

// Custom styled components
import {
  CommandCenterContainer,
  CommandCenterOuterContainer,
  CommandCenterPageHeader,
  CommandCenterLayout,
  CommandCenterMainContent,
  CommandCenterCriticalAlert,
  CommandCenterAlertContent,
  CommandCenterAlertIcon,
  CommandCenterAlertText,
  CommandCenterAlertStats,
  CommandCenterAlertStat,
  CommandCenterSitesSection,
  CommandCenterSitesTitle,
  CommandCenterSitesGrid,
  CommandCenterSiteCard,
  CommandCenterSiteHeader,
  CommandCenterSiteName,
  CommandCenterSiteSubtitle,
  CommandCenterSiteStatus,
  CommandCenterSiteStats,
  CommandCenterSiteOnline,
  CommandCenterLoadingMessage,
  CommandCenterErrorMessage,
  CommandCenterPrimaryButton,
  CommandCenterSpinner,
} from './CommandCenter.styles';




 const generateColors = (count) => {
    const colors = [];
    const baseColors = [
      '#3b82f6', // Blue
      '#8b5cf6', // Purple
      '#ec4899', // Pink
      '#f97316', // Orange
      '#10b981', // Green
      '#06b6d4', // Cyan
      '#8b5a2b', // Brown
      '#6366f1', // Indigo
      '#84cc16', // Lime
      '#f59e0b', // Amber
      '#d946ef', // Fuchsia
    ];

    // If we need more colors than base colors, generate additional ones
    for (let i = 0; i < count; i++) {
      if (i < baseColors.length) {
        colors.push(baseColors[i]);
      } else {
        // Generate additional colors using HSL
        const hue = (i * 137.508) % 360; // Golden angle approximation for good distribution
        const saturation = 65 + (i % 3) * 10; // Vary saturation slightly
        const lightness = 50 + (i % 4) * 5; // Vary lightness slightly
        colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
      }
    }
    return colors;
  };

  
/**
 * Utility function to determine site status based on device data
 * @param {Array} devices - Array of devices for the site
 * @param {number} siteIndex - Index of the site in apiSites array
 * @param {number} totalSites - Total number of sites for color generation
 * @returns {Object} - Status object with status, color, and uptime
 */
function getSiteStatus(devices, siteIndex, totalSites) {
  if (!devices || devices.length === 0) {
    return { status: 'No Data', color: '#6c757d', uptime: 0 };
  }

  const totalDevices = devices.length;
  const onlineDevices = devices.filter(device =>
    device.status?.toLowerCase() === 'online'
  ).length;
  const offlineDevices = totalDevices - onlineDevices;

  const uptime = totalDevices > 0 ? ((onlineDevices / totalDevices) * 100) : 0;

  // Generate dynamic colors for all sites
  //const siteColors = generateColors(totalSites);
  //const siteColor = siteColors[siteIndex] || '#3b82f6'; // Fallback color

  // Determine status based on offline devices count
  // if (offlineDevices === 0) {
  //   return { status: 'Healthy', color: '#28A745', uptime };
  // } else if (offlineDevices === 1) {
  //   return { status: '1 down', color: '#FFC107', uptime };
  // } else if (offlineDevices > 1) {
  //   return { status: `${offlineDevices} down`, color: '#DC3545', uptime };
  // } else {
  //   return { status: 'Healthy', color: '#28A745', uptime };
  // }
// Success: #28A745 (Green)
// Warning: #FFC107 (Amber/Yellow)
// Error: #DC3545 (Red)
  if (onlineDevices === totalDevices) {
    return { status: 'Healthy', color: '#28A745', uptime };
  } 
   if (onlineDevices === 0) {
    return { status: 'down', color: '#DC3545', uptime };
   }
  return{
    status: `${offlineDevices} down`, color: '#FFC107', uptime
  };
   
}

/**
 * Utility function to count critical outages across all sites
 * @param {Array} allDevices - Array of all devices
 * @returns {Object} - Outage statistics
 */
function getCriticalOutageStats(allDevices) {
  if (!allDevices || allDevices.length === 0) {
    return { total: 0, online: 0, offline: 0, affectedSites: 0 };
  }

  const total = allDevices.length;
  const online = allDevices.filter(device =>
    device.status?.toLowerCase() === 'online'
  ).length;
  const offline = total - online;

  // Count unique sites with offline devices
  // const sitesWithOfflineDevices = new Set();
  // allDevices.forEach((device) => {
  //   if (device.status?.toLowerCase() === "offline" && device.store_id) {
  //     sitesWithOfflineDevices.add(device.store_id);
  //   }
  // });

  // Store each affected store_id only once, along with store_name
  const affectedStoreNames = new Map();
  allDevices.forEach((device) => {
    if (device.status?.toLowerCase() === "offline" && device.store_id) {
      if (!affectedStoreNames.has(device.store_id)) {
        affectedStoreNames.set(device.store_id, device.store_name || "");
      }
    }
  });

  //count physical site from store_name
  const affectedSites = Array.from(affectedStoreNames.values()).reduce(
    (count, storeName) => {
      const siteNameCount = (storeName || "")
        .split("/")
        .map((name) => name.trim())
        .filter(Boolean).length;
      return count + (siteNameCount || 1);
    },
    0
  );

  return {
    total,
    online,
    offline,
    //affectedSites: sitesWithOfflineDevices.size,
   affectedSites
  };
}

export default function CommandCenter() {
  // ========================================
  // STATE MANAGEMENT
  // ========================================

  // API Data State - Stores responses from backend APIs
  const [sitesData, setSitesData] = useState(null); // Sites hierarchy data from getSitesApi
  const [allDevicesData, setAllDevicesData] = useState([]); // All devices data from getDevicesApi

  // Loading States - Controls loading indicators
  const [loading, setLoading] = useState(true); // Initial page load

  // Error States - Separate error handling for different API calls
  const [error, setError] = useState(''); // Sites API or general errors


  // ========================================
  // REFS AND REAL-TIME POLLING
  // ========================================

  // Prevent duplicate API calls in React StrictMode (development)
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const isInitialMount = useRef(true);

  // ========================================
  // API FUNCTIONS
  // ========================================

  /**
   * Fetches sites hierarchy data from the backend
   * Called once on component mount to populate the site/location tree
   * @returns {Promise<boolean>} - Returns Promise that resolves to true if successful, false if failed
   */
  const fetchSitesData = async () => {
    try {
      setError(''); // Clear previous errors
      const response = await getSitesApi();
      setSitesData(response);
      return true; // Success
    } catch (err) {
      console.error('Error fetching sites:', err);
      setError(err.message);
      return false; // Failure
    }
  };

  /**
   * Fetches all devices data for command center overview
   */
  const fetchDevicesData = async () => {
    try {
      // Call API to get all devices across all sites
      const response = await getDevicesApi({ all_sites: true });
      console.log('Command Center devices API response:', response);

      if (response?.devices) {
        setAllDevicesData(response.devices);
      } else {
        console.warn('No devices data received');
        setAllDevicesData([]);
      }
    } catch (err) {
      console.error('Error fetching devices:', err);
      setAllDevicesData([]);
    }
  };

  /**
   * Initial data loading function - fetches both sites and devices
   * Called on component mount and when user clicks "Try Again" on errors
   */
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const sitesSuccess = await fetchSitesData(); // Load sites hierarchy first

      if (sitesSuccess) {
        await fetchDevicesData(); // Load all devices only if sites loaded successfully
      }
    } catch (err) {
      // Errors are handled in individual fetch functions
      console.error('Error in fetchAllData:', err);
    } finally {
      setLoading(false);
    }
  }, []);


  // ========================================
  // EFFECTS
  // ========================================

  /**
   * Initial data loading effect - runs once on component mount
   * Prevents duplicate API calls in React StrictMode
   */
  useEffect(() => {
    if (isInitialMount.current && !hasFetchedData) {
      isInitialMount.current = false;
      setHasFetchedData(true);
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once

  // ========================================
  // COMPUTED DATA AND MEMOIZED VALUES
  // ========================================

  /**
   * Transforms API sites data into normalized format for UI consumption
   */
  const apiSites = useMemo(() => {
    if (!sitesData?.AllSites) return [];

    return sitesData.AllSites.map(site => ({
      id: site.store_id,
      name: site.store_name,
      locations: site.locations || []
    }));
  }, [sitesData]);

  /**
   * Calculates site-wise device data and status
   */
  const siteStatusData = useMemo(() => {
    if (!apiSites.length || !allDevicesData.length) return [];

    return apiSites.map((site, index) => {
      // Get devices for this site
      const siteDevices = allDevicesData.filter(device => device.store_id === site.id);
      const status = getSiteStatus(siteDevices, index, apiSites.length);


      // Get location name from locations array (use first location if multiple)
      const locationName = site.locations && site.locations.length > 0
        ? site.locations[0].location_name || site.locations[0].name
        : '';
      const offlineDevicesCount = siteDevices.filter(d => d.status?.toLowerCase() === 'offline').length;
      const onlineDevicesCount = siteDevices.filter(d => d.status?.toLowerCase() === 'online').length;
      const totalDevicesCount = siteDevices.length;
      const uptimePercentage = totalDevicesCount > 0 ? (onlineDevicesCount / totalDevicesCount) * 100 : 0;
     
      return {
        id: site.id,
        name: site.name,
        subtitle: site.locations.length > 1 ? 'All Locations' : locationName,
        status: status.status,
        statusColor: status.color,
        uptime: uptimePercentage,
        totalDevices: totalDevicesCount,
        onlineDevices: onlineDevicesCount,
        offlineDevices: offlineDevicesCount,
      };
    }).filter(site => site.totalDevices > 0); // Only show sites with devices
  }, [apiSites, allDevicesData]);


  const physicalSiteCount=useMemo(
    ()=>{
      return siteStatusData.reduce((count, site) => {
        const siteNameCount=(site.name || '')
        .split('/')
        .map(name=>name.trim())
        .filter(Boolean)
        .length;
        return count + (siteNameCount|| 1);
      },0);
    },[siteStatusData]);

  /**
   * Generate detailed tooltip content for site hover
   */
  const generateSiteTooltip = (site) => {
    // Find the original site data to get all locations
    const originalSite = apiSites.find(s => s.id === site.id);
    const siteDevices = allDevicesData.filter(device => device.store_id === site.id);

    if (!originalSite?.locations?.length) {
      return `Site: ${site.name}\nTotal Devices: ${site.totalDevices}\nOnline: ${site.onlineDevices}\nOffline: ${site.offlineDevices}`;
    }

    let tooltipContent = '';
    
    // Build dynamic locationDeviceMap from API data
    const locationDeviceMap = {};
    
    // Group devices by location_id from the API data
    originalSite.locations.forEach(location => {
      const locationDevices = siteDevices.filter(device => device.location_id === location.location_id);
      const offlineDevices = locationDevices.filter(device => device.status?.toLowerCase() === 'offline');
      
      locationDeviceMap[location.location_name] = {
        total: locationDevices.length,
        offline: offlineDevices.length
      };
    });
    
    // Use all locations from API (no filtering)
    const validLocations = originalSite.locations;
    
    console.log('=== TOOLTIP DEBUG ===');
    console.log('Site:', site.name);
    console.log('Total siteDevices:', siteDevices.length);
    console.log('Dynamic locationDeviceMap:', locationDeviceMap);
    console.log('All locations from API:', originalSite.locations.map(loc => `${loc.location_name} (ID: ${loc.location_id})`));
    
    validLocations.forEach((location, index) => {
      const locationData = locationDeviceMap[location.location_name];
      
      if (locationData) {
        const deviceCount = locationData.total;
        const offlineCount = locationData.offline;
        
        console.log(`${location.location_name}: ${deviceCount} devices, ${offlineCount} offline`);
        
        if (index > 0) tooltipContent += '\n';
        tooltipContent += `Location: ${location.location_name}, Devices found: ${deviceCount}, offline devices: ${offlineCount}`;
      }
    });

    console.log('=== END DEBUG ===');
    return tooltipContent;
  };

  /**
   * Calculates critical outage statistics
   */
  const criticalOutageStats = useMemo(() => {
    return getCriticalOutageStats(allDevicesData);
  }, [allDevicesData]);

  // ========================================
  // RENDER LOGIC
  // ========================================

  // Initial loading state
  if (loading) {
    return (
      <CommandCenterContainer>
        <CommandCenterOuterContainer>
           <CommandCenterPageHeader>
              <Typography variant="h2" component="h2">
                Command Center
              </Typography>
              <Typography className="subtitle">
                  Loading...
                   </Typography>
            </CommandCenterPageHeader>
          <Box sx={{
            marginTop: '20px',
            backgroundColor: 'white', 
            border: 'none',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <CommandCenterLoadingMessage>
              <CommandCenterSpinner sx={{ marginRight: '8px' }} />
              Loading command center data...
            </CommandCenterLoadingMessage>
          </Box>
        </CommandCenterOuterContainer>
      </CommandCenterContainer>
    );
  }

  // Error state (sites API or general errors)
  if (error) {
    return (
      <CommandCenterContainer>
        <CommandCenterOuterContainer>
          <CommandCenterPageHeader>
            <Typography variant="h2" component="h2">
              Command Center
            </Typography>
            <Typography className="subtitle">
              Ford power infrastructure
            </Typography>
          </CommandCenterPageHeader>

          <Box sx={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '24px'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <Typography sx={{ fontSize: '24px', marginRight: '12px' }}>⚠️</Typography>
              <Typography sx={{
                color: '#ef4444',
                fontSize: '1.1rem',
                fontWeight: '600'
              }}>
                API Error
              </Typography>
            </Box>

            <CommandCenterErrorMessage component="div" sx={{
              color: '#ef4444',
              fontSize: '1rem',
              marginBottom: '20px',
              textAlign: 'left'
            }}>
              {error}
            </CommandCenterErrorMessage>

            <Box sx={{ textAlign: 'center' }}>
              <CommandCenterPrimaryButton onClick={fetchAllData}>
                Try Again
              </CommandCenterPrimaryButton>
            </Box>
          </Box>
        </CommandCenterOuterContainer>
      </CommandCenterContainer>
    );
  }

  // Main command center render
  return (
    <CommandCenterContainer>
      <CommandCenterOuterContainer>
        <CommandCenterLayout>
          <CommandCenterMainContent>
            {/* Page Header */}
            <CommandCenterPageHeader>
              <Typography variant="h2" component="h2">
                Command Center
              </Typography>
              <Typography className="subtitle">
                  Ford power infrastructure - live across {physicalSiteCount} sites
              </Typography>
            </CommandCenterPageHeader>



            {/* Critical Outage Alert Banner */}
            {criticalOutageStats.offline > 0 ? (
              <CommandCenterCriticalAlert>
                <CommandCenterAlertContent>
                  <CommandCenterAlertIcon>
                    <WarningIcon />
                  </CommandCenterAlertIcon>
                  <CommandCenterAlertText>
                    Critical outage: Devices are offline at {criticalOutageStats.affectedSites} sites, affecting operations.
                    {/* {criticalOutageStats.affectedSites} critical outage — critical systems affected */}
                  </CommandCenterAlertText>
                  <CommandCenterAlertStats>
                    <CommandCenterAlertStat>
                      <Box sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                        {criticalOutageStats.total}
                      </Box>
                      <Box sx={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
                        TOTAL
                      </Box>
                    </CommandCenterAlertStat>
                    <CommandCenterAlertStat>
                      <Box sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                        {criticalOutageStats.online}
                      </Box>
                      <Box sx={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
                        ONLINE
                      </Box>
                    </CommandCenterAlertStat>
                    <CommandCenterAlertStat>
                      <Box sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                        {criticalOutageStats.offline}
                      </Box>
                      <Box sx={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
                        OFFLINE
                      </Box>
                    </CommandCenterAlertStat>
                  </CommandCenterAlertStats>
                </CommandCenterAlertContent>
              </CommandCenterCriticalAlert>
            ) : (
              <Box sx={{
                backgroundColor: '#28a745',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: '#ffffff',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: '16px', md: 0 },
                  alignItems: { xs: 'flex-start', md: 'center' }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', marginRight: '16px' }}>
                      <Box sx={{ fontSize: '2rem', marginRight: '12px' }}>✓</Box>
                    </Box>
                    <Box sx={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      color: '#ffffff',
                      flex: 1
                    }}>
                      Power Infrastructure Operating Normally – No Active Outages Detected Across All Sites.
                    </Box>
                  </Box>
                  <Box sx={{
                    display: 'flex',
                    gap: '32px',
                    flexDirection: { xs: 'row', md: 'row' },
                    width: { xs: '100%', md: 'auto' },
                    justifyContent: { xs: 'space-around', md: 'flex-end' }
                  }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      color: '#ffffff',
                      minWidth: '60px'
                    }}>
                      <Box sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                        {criticalOutageStats.total}
                      </Box>
                      <Box sx={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
                        TOTAL
                      </Box>
                    </Box>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      color: '#ffffff',
                      minWidth: '60px'
                    }}>
                      <Box sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                        {criticalOutageStats.online}
                      </Box>
                      <Box sx={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
                        ONLINE
                      </Box>
                    </Box>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      color: '#ffffff',
                      minWidth: '60px'
                    }}>
                      <Box sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                        {criticalOutageStats.offline}
                      </Box>
                      <Box sx={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
                        OFFLINE
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Sites at a Glance Section */}
            <CommandCenterSitesSection>
              <CommandCenterSitesTitle>
                <Typography variant="h4" component="h3">
                  Sites at a glance
                </Typography>
              </CommandCenterSitesTitle>

              <CommandCenterSitesGrid>
                <Grid container spacing={1}>
                  {siteStatusData.map((site) => (
                    <Grid item xs={12} sm={6} md={4} key={site.id}>
                      <Tooltip 
                        title={
                          <Box sx={{ whiteSpace: 'pre-line', fontSize: '0.875rem' }}>
                            {generateSiteTooltip(site)}
                          </Box>
                        }
                        placement="top"
                        arrow
                        sx={{
                          '& .MuiTooltip-tooltip': {
                            backgroundColor: '#1a1a1a',
                            color: '#ffffff',
                            fontSize: '0.875rem',
                            maxWidth: 300,
                            padding: '12px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                          },
                          '& .MuiTooltip-arrow': {
                            color: '#1a1a1a'
                          }
                        }}
                      >
                        <CommandCenterSiteCard statusColor={site.statusColor}>
                          <CommandCenterSiteHeader>
                            <Box>
                              <CommandCenterSiteName>
                                {site.name}
                              </CommandCenterSiteName>
                              <CommandCenterSiteSubtitle>
                                {site.subtitle}
                              </CommandCenterSiteSubtitle>
                            </Box>
                            <CommandCenterSiteStatus statusColor={site.statusColor}>
                              {site.status}
                            </CommandCenterSiteStatus>
                          </CommandCenterSiteHeader>

                          <CommandCenterSiteStats>
                            <Box sx={{  fontFamily: FORD_TYPOGRAPHY.newfont, fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                              <CommandCenterSiteOnline>
                                {site.onlineDevices}/{site.totalDevices} online
                              </CommandCenterSiteOnline>
                              <Box sx={{ 
                                // fontSize: '12px', 
                                fontWeight: 600, 
                                color: (site.statusColor?.startsWith('#') || site.statusColor?.startsWith('hsl(')) ? site.statusColor : '#666666',
                                // fontFamily: FORD_TYPOGRAPHY.newfont

                              }}>
                                {site.uptime.toFixed(1)}% uptime
                              </Box>
                            </Box>

                            {/* Progress Bar */}
                            <Box sx={{
                              width: '100%',
                              height: '8px',
                              backgroundColor: '#e0e0e0',
                              borderRadius: '4px',
                              overflow: 'hidden'
                            }}>
                              <Box sx={{
                                width: `${site.totalDevices > 0 ? (site.onlineDevices / site.totalDevices) * 100 : 0}%`,
                                height: '100%',
                                 backgroundColor: (site.statusColor?.startsWith('#') || site.statusColor?.startsWith('hsl(')) ? site.statusColor : '#666666',
                                transition: 'width 0.3s ease-in-out'
                              }} />
                            </Box>
                          </CommandCenterSiteStats>
                        </CommandCenterSiteCard>
                      </Tooltip>
                    </Grid>
                  ))}
                </Grid>
              </CommandCenterSitesGrid>
            </CommandCenterSitesSection>
          </CommandCenterMainContent>
        </CommandCenterLayout>
      </CommandCenterOuterContainer>
    </CommandCenterContainer>
  );
}
